/**
 * ResearchAnalysisCard — renders Phase 3 ResearchAnalysis output. Shows
 * keyFacts table with confidence / category chips, contentSeeds cards (each
 * a ready-to-use hook angle), audience relevance list, thematic coverage
 * bars, contradictions, outdated sources callouts.
 *
 * Sprint 18 MVP — scannable read-only view. Future UX may allow:
 *   - "Use this seed" action that pre-fills TopicPostGenerationDialog
 *   - Edit/reject individual keyFacts
 *   - Diff vs previous run's analysis
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarIcon from '@mui/icons-material/Star';
import { insightFundService } from '../../services/InsightFundService';

export interface ResearchAnalysisCardProps {
  analysis: any;
  /** Task 7 — when set, enables Promote-to-Insight-Fund actions on
   *  promotable items. Without these, items render read-only. */
  projectId?: string;
  runId?: string;
  userId?: string;
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  statistic: { bg: '#e3f2fd', text: '#1565c0' },
  quote: { bg: '#fff3e0', text: '#e65100' },
  framework: { bg: '#f3e5f5', text: '#7b1fa2' },
  case_study: { bg: '#e8f5e9', text: '#2e7d32' },
  counter_evidence: { bg: '#fce4ec', text: '#c62828' },
};

const CONFIDENCE_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

export const ResearchAnalysisCard: React.FC<ResearchAnalysisCardProps> = ({
  analysis, projectId, runId, userId,
}) => {
  const [expandedFacts, setExpandedFacts] = useState<Record<string, boolean>>({});
  const [expandedSeeds, setExpandedSeeds] = useState<Record<string, boolean>>({});
  const [promoted, setPromoted] = useState<Record<string, boolean>>({});
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const keyFacts = analysis?.keyFacts ?? [];
  const contentSeeds = analysis?.contentSeeds ?? [];
  const audienceRelevance = analysis?.audienceRelevance ?? [];
  const thematicCoverage = analysis?.thematicCoverage ?? [];
  const contradictions = analysis?.contradictions ?? [];
  const outdatedSources = analysis?.outdatedSources ?? [];
  const quotes = analysis?.quotes ?? [];
  const insightCandidates = analysis?.insightCandidates ?? [];
  const canPromote = Boolean(projectId);

  const promote = async (
    promoteKey: string,
    payload: { content: string; tags?: string[]; fieldPath: string },
  ) => {
    if (!canPromote || !projectId) return;
    setPromoteError(null);
    try {
      await insightFundService.create(projectId, {
        projectId,
        content: payload.content,
        tags: payload.tags,
        source: {
          kind: runId ? 'research_run' : 'manual',
          runId,
          fieldPath: payload.fieldPath,
        },
        promotedBy: userId,
      });
      setPromoted((prev) => ({ ...prev, [promoteKey]: true }));
    } catch (e) {
      setPromoteError((e as Error).message);
    }
  };

  const factsById = useMemo(() => {
    const map = new Map<string, any>();
    for (const f of keyFacts) map.set(f.id, f);
    return map;
  }, [keyFacts]);

  return (
    <Stack spacing={3} data-testid="research-analysis-card">
      {promoteError && (
        <Alert severity="error" onClose={() => setPromoteError(null)}>
          {promoteError}
        </Alert>
      )}
      {/* ── Content seeds ── */}
      {contentSeeds.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Content seeds ({contentSeeds.length})
          </Typography>
          <Stack spacing={1}>
            {contentSeeds.map((seed: any, sIdx: number) => {
              const open = expandedSeeds[seed.id];
              const promoteKey = `seed:${seed.id}`;
              const isPromoted = promoted[promoteKey];
              return (
                <Box
                  key={seed.id}
                  sx={{
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                      🔗 {seed.angle}
                    </Typography>
                    {canPromote && seed.promotable && (
                      <Tooltip title={isPromoted ? 'Promoted' : 'Promote to Insight Fund'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isPromoted}
                            onClick={() => promote(promoteKey, {
                              content: `${seed.angle}${seed.hook ? ` — hook: "${seed.hook}"` : ''}`,
                              tags: seed.suggestedThemeIds ?? [],
                              fieldPath: `analysis.contentSeeds[${sIdx}]`,
                            })}
                            data-testid={`promote-seed-${seed.id}`}
                          >
                            {isPromoted ? <StarIcon fontSize="small" color="primary" /> : <StarOutlineIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedSeeds((prev) => ({ ...prev, [seed.id]: !prev[seed.id] }))
                      }
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={Boolean(open)}>
                    <Box sx={{ pl: 1, pt: 1 }}>
                      {seed.primaryFactIds?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Anchored facts:
                          </Typography>
                          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                            {seed.primaryFactIds.map((fid: string) => {
                              const f = factsById.get(fid);
                              if (!f) return null;
                              return (
                                <Typography key={fid} variant="caption" sx={{ display: 'block' }}>
                                  • {f.claim}
                                </Typography>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                      {seed.supportingAudienceIds?.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {seed.supportingAudienceIds.map((a: string) => (
                            <Chip key={a} size="small" variant="outlined" label={a} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Key facts ── */}
      {keyFacts.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Key facts ({keyFacts.length})
          </Typography>
          <Stack spacing={0.75}>
            {keyFacts.map((fact: any, fIdx: number) => {
              const open = expandedFacts[fact.id];
              const color = CATEGORY_COLORS[fact.category] ?? { bg: '#eee', text: '#555' };
              const promoteKey = `fact:${fact.id}`;
              const isPromoted = promoted[promoteKey];
              return (
                <Box
                  key={fact.id}
                  sx={{
                    p: 1,
                    borderLeft: '2px solid',
                    borderColor: color.text,
                    bgcolor: 'action.hover',
                    borderRadius: 0.25,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={fact.category}
                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: color.bg, color: color.text }}
                    />
                    <Chip
                      size="small"
                      color={CONFIDENCE_COLOR[fact.confidence] ?? 'default'}
                      label={fact.confidence}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {fact.datePublished && (
                      <Typography variant="caption" color="text.secondary">
                        {fact.datePublished}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {canPromote && fact.promotable && (
                      <Tooltip title={isPromoted ? 'Promoted' : 'Promote to Insight Fund'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isPromoted}
                            onClick={() => promote(promoteKey, {
                              content: fact.claim,
                              tags: [fact.category].filter(Boolean),
                              fieldPath: `analysis.keyFacts[${fIdx}]`,
                            })}
                            data-testid={`promote-fact-${fact.id}`}
                          >
                            {isPromoted
                              ? <StarIcon fontSize="small" color="primary" />
                              : <StarOutlineIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedFacts((prev) => ({ ...prev, [fact.id]: !prev[fact.id] }))
                      }
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Typography variant="body2">{fact.claim}</Typography>
                  <Collapse in={Boolean(open)}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}
                    >
                      id: {fact.id} · sources: chunks [{(fact.sourceChunkIds ?? []).join(', ')}]
                    </Typography>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Quotes (Task 3 — enriched Phase 3) ── */}
      {quotes.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Quotes ({quotes.length})
          </Typography>
          <Stack spacing={0.75}>
            {quotes.map((q: any, qIdx: number) => {
              const promoteKey = `quote:${q.id ?? qIdx}`;
              const isPromoted = promoted[promoteKey];
              return (
                <Box
                  key={q.id ?? qIdx}
                  sx={{
                    p: 1, borderLeft: '2px solid', borderColor: 'warning.main',
                    bgcolor: 'action.hover', borderRadius: 0.25,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{q.text}"
                      </Typography>
                      {q.attribution && (
                        <Typography variant="caption" color="text.secondary">
                          — {q.attribution}
                          {q.sourceUrl ? ` · ${q.sourceUrl}` : ''}
                        </Typography>
                      )}
                    </Box>
                    {canPromote && q.promotable && (
                      <Tooltip title={isPromoted ? 'Promoted' : 'Promote to Insight Fund'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isPromoted}
                            onClick={() => promote(promoteKey, {
                              content: `"${q.text}"${q.attribution ? ` — ${q.attribution}` : ''}`,
                              tags: ['quote'],
                              fieldPath: `analysis.quotes[${qIdx}]`,
                            })}
                          >
                            {isPromoted ? <StarIcon fontSize="small" color="primary" /> : <StarOutlineIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Insight candidates (Task 3 — explicit promote-targeted) ── */}
      {insightCandidates.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Insight candidates ({insightCandidates.length})
          </Typography>
          <Stack spacing={0.75}>
            {insightCandidates.map((it: any, iIdx: number) => {
              const promoteKey = `insight:${it.id ?? iIdx}`;
              const isPromoted = promoted[promoteKey];
              return (
                <Box
                  key={it.id ?? iIdx}
                  sx={{
                    p: 1, borderLeft: '2px solid', borderColor: 'success.main',
                    bgcolor: 'action.hover', borderRadius: 0.25,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{it.content}</Typography>
                      {(it.tags ?? []).length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          {(it.tags ?? []).map((t: string) => (
                            <Chip key={t} size="small" label={t} variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </Box>
                    {canPromote && (
                      <Tooltip title={isPromoted ? 'Promoted' : 'Promote to Insight Fund'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isPromoted}
                            onClick={() => promote(promoteKey, {
                              content: it.content,
                              tags: it.tags ?? [],
                              fieldPath: `analysis.insightCandidates[${iIdx}]`,
                            })}
                          >
                            {isPromoted ? <StarIcon fontSize="small" color="primary" /> : <StarOutlineIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Audience relevance ── */}
      {audienceRelevance.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Audience relevance ({audienceRelevance.length})
          </Typography>
          <Stack spacing={0.75}>
            {audienceRelevance.map((entry: any) => (
              <Box
                key={entry.audienceId}
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip size="small" variant="outlined" label={entry.audienceId} />
                  <Typography variant="caption" color="text.secondary">
                    top {(entry.topFactIds ?? []).length} facts
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {entry.whyItMatters}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── Thematic coverage ── */}
      {thematicCoverage.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Thematic coverage
          </Typography>
          <Stack spacing={0.75}>
            {thematicCoverage.map((t: any) => (
              <Box key={t.themeId}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                  <Chip size="small" variant="outlined" label={t.themeId} />
                  <Typography variant="caption" color="text.secondary">
                    {(t.coverageScore * 100).toFixed(0)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, (t.coverageScore ?? 0) * 100))}
                  sx={{ height: 6, borderRadius: 0.5 }}
                />
                {(t.gaps ?? []).length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                    Gaps: {t.gaps.join('; ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── Contradictions & outdated ── */}
      {(contradictions.length > 0 || outdatedSources.length > 0) && (
        <Box>
          {contradictions.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {contradictions.length} contradiction(s) detected between sources
            </Alert>
          )}
          {outdatedSources.length > 0 && (
            <Alert severity="info">
              {outdatedSources.length} source(s) flagged as outdated
            </Alert>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default ResearchAnalysisCard;
